import * as React from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'src/rerouter';
import useLabelTranslation from 'src/hooks/useLabelTranslation';
import {
  CONSTANTS,
  getTranslatedText,
} from 'src/components/authentication/contants';
import {
  ERROR_CODES,
  getTranslatedError,
} from 'src/components/authentication/errors';

import {
  BodyLarge,
  TitleMedium,
  BodyMedium,
  ButtonTextMedium,
} from '@spaced-out/ui-design-system/lib/components/Text';
import {Input} from '@spaced-out/ui-design-system/lib/components/Input';
import {Button} from '@spaced-out/ui-design-system/lib/components/Button';
import {InContextAlert} from '@spaced-out/ui-design-system/lib/components/InContextAlert';

import {signUpUser} from 'src/action-creators/authentication';

import {selectSignUpError} from 'src/selectors/authentication';
import PasswordInstruction from 'src/components/authentication/common/password-instruction/password-instruction';
import PasswordConfirmation from 'src/components/authentication/common/password-confirmation/password-confirmation';

import type {SignUpFormProps} from 'src/types/authentication';

import css from './sign-up.css';
import maincss from 'src/components/authentication/main.css';


const SignUpForm = (props: SignUpFormProps) => {
  const {t} = useLabelTranslation();
  const {inviteEmail, inviteCode, inviteCodeError} = props;

  const router = useHistory();
  const dispatch = useDispatch();

  const signUpError = useSelector(selectSignUpError);

  const [fullName, setFullName] = React.useState<string>('');
  const [email, setEmail] = React.useState<string>('');
  const [passwordStrength, setPasswordStrength] = React.useState<number>(0);
  const [newPassword, setNewPassword] = React.useState<string>('');
  const [confirmPassword, setConfirmPassword] = React.useState<string>('');
  const [disableSignUp, setDisableSignUp] = React.useState<boolean>(true);
  const [isSigningUp, setIsSigningUp] = React.useState<boolean>(false);
  const [isNameValid, setIsNameValid] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (passwordStrength < 3) {
      setDisableSignUp(true);
    } else if (newPassword !== confirmPassword) {
      setDisableSignUp(true);
    } else if (!fullName) {
      setDisableSignUp(true);
    } else {
      setDisableSignUp(false);
    }
  }, [passwordStrength, newPassword, confirmPassword, fullName]);

  const setName = (value: string) => {
    setFullName(value);
    setIsNameValid(!!value);
  };

  const goToSignIn = () => {
    router.replace('/signin');
  };

  const handleSignUp = async () => {
    setIsSigningUp(true);
    const apiResponse = await dispatch(
      signUpUser(fullName, inviteEmail || email, newPassword, inviteCode),
    );
    // @ts-expect-error
    if (apiResponse) {
      const {host, protocol} = window.location;
      const baseDomain = host.substring(host.indexOf('.') + 1);
      // @ts-expect-error - TS2339 - Property 'agency' does not exist on type 'ThunkAction<void>'.
      if (apiResponse?.agency?.mfa_auth_channel) {
        const path = '/signin?fromSignUp=true';
        router.replace(path);
      } else {
        // @ts-expect-error - TS2339 - Property 'agency' does not exist on type 'ThunkAction<void>'.
        const path = `${protocol}//${apiResponse.agency.slug}.${baseDomain}`;
        // @ts-expect-error - TS2322 - Type 'string' is not assignable to type 'string & Location'.
        window.location = path;
      }
    }
    setIsSigningUp(false);
  };

  return (
    <div className={maincss.contentSideBar}>
      <div className={css.signUpcontainerContent}>
        <div className={css.signUpSection}>
          <TitleMedium>
            {getTranslatedText(t, CONSTANTS.SIGNUP_TITLE)}
          </TitleMedium>
          <BodyLarge className={maincss.subText}>
            {getTranslatedText(t, CONSTANTS.SIGNUP_SUBTEXT)}
          </BodyLarge>
          {inviteCodeError && (
            <InContextAlert semantic="danger" classNames={{wrapper: css.alert}}>
              {/* @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'. */}
              {getTranslatedError(t, ERROR_CODES[inviteCodeError])}
            </InContextAlert>
          )}
          <div className={css.input}>
            <Input
              label={t('Name', 'Name')}
              placeholder={t('Enter_full_name', 'Enter full name')}
              size="medium"
              type="text"
              value={fullName}
              error={!isNameValid}
              errorText={getTranslatedText(t, CONSTANTS.INVALID_FULL_NAME)}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div className={css.input}>
            <Input
              label={t('Email', 'Email')}
              placeholder={t('Enter_email', 'Enter email')}
              size="medium"
              type="text"
              value={inviteEmail || email}
              disabled={!!inviteEmail}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className={css.instructions}>
            <PasswordInstruction />
          </div>
          <div className={css.confirmation}>
            <PasswordConfirmation
              passwordLabel={t('New_password', 'New password')}
              passwordPlaceholder={t('Enter_password', 'Enter password')}
              confirmPasswordLabel={t('Confirm_password', 'Confirm password')}
              confirmPasswordPlaceholder={t(
                'Repeat_password',
                'Repeat password',
              )}
              setPasswordStrengthValue={setPasswordStrength}
              setNewPasswordValue={setNewPassword}
              setConfirmPasswordValue={setConfirmPassword}
            />
          </div>
          <Button
            size="medium"
            type="primary"
            classNames={{wrapper: css.signUpBtn}}
            disabled={disableSignUp || isSigningUp}
            onClick={handleSignUp}
          >
            {isSigningUp
              ? t('Signing_up', 'Signing up')
              : t('Sign_up', 'Sign up')}
          </Button>
          {signUpError && (
            <InContextAlert semantic="danger" classNames={{wrapper: css.alert}}>
              {getTranslatedError(
                t,
                // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly InvalidLoginAttempt: "Incorrect email or password"; readonly ServerError: "Failed to perform action, Please try after sometime"; readonly IncorrectOTP: "Invalid OTP entered"; ... 13 more ...; readonly InvalidReferrerEmail: "Invalid Referrer Email"; }'.
                ERROR_CODES[signUpError] || ERROR_CODES['GenericSignUpError'],
              )}
            </InContextAlert>
          )}
          <div className={css.signInSection}>
            <BodyMedium className={css.forgotLoginText}>
              {t('Or', 'Or')}
            </BodyMedium>
            <ButtonTextMedium
              color="clickable"
              className={css.signInLink}
              // @ts-expect-error - TS2769 - No overload matches this call.
              onClick={goToSignIn}
            >
              {t('Sign_in', 'Sign in')}
            </ButtonTextMedium>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpForm;
